import { RewardModel, ItemRewardModel, MonsterRewardModel } from './reward.model';

export class IntegrationTypes {
  public static award = 'integrationAward';
  public static competitionAward = 'integrationCompetitionAward';
  public static exam = 'integrationExam';
  public static location = 'integrationLocation';
  public static passcode = 'integrationPasscode';
  public static videoHelp = 'integrationVideoHelp';

  public static list(): string[] {
    return [
      this.award,
      this.competitionAward,
      this.exam,
      this.location,
      this.passcode,
      this.videoHelp
    ];
  }
}

export class RewardGrantTypes {
  public static grantAll = 'grantAll';
  public static pickOne = 'pickOne';

  public static list(): string[] {
    return [
      this.grantAll,
      this.pickOne
    ];
  }
}

export class CompetitionAwardTypes {
  public static award = 'award';
  public static obstacleCourse = 'obstacleCourse';

  public static list(): string[] {
    return [
      this.award,
      this.obstacleCourse
    ];
  }
}

export class ObstacleCourseLevels {
  public static none = 'none';
  public static beach = 'beach';
  public static desert = 'desert';

  /**
   * The levels an obstacle course can be set to. 'none' is the default for a plain award.
   */
  public static list(): string[] {
    return [
      this.beach,
      this.desert
    ];
  }
}

export class QRCodeContentTypes {
  public static json = 'json';
  public static url = 'url';

  public static list(): string[] {
    return [
      this.url,
      this.json
    ];
  }
}

export class IntegrationModel {

  public _id: string;
  public name: string;
  public integrationType: string;
  public visualType: string;
  public contentType: string;
  public country: string;
  public region: string;
  public minimumGameVersionNumber: string;
  public rewardGrantType: string = RewardGrantTypes.grantAll;
  public numUsesPerStudent = 1;
  public redeemEndAt: Date = null;
  public rewards: RewardModel[] = [];
  public createdAt: Date;
  public updatedAt: Date;

  static generate(json: any): IntegrationModel {
    return this.generateModel(json);
  }

  static generateList(jsonList: any[]): IntegrationModel[] {
    return this.generateModels(jsonList);
  }

  static generateModel(json: any): IntegrationModel {

    // Use the discriminator model based on the integration type
    let integration: IntegrationModel;
    if (json.integrationType === IntegrationTypes.competitionAward) {
      integration = new IntegrationCompetitionAwardModel();
    } else if (json.integrationType === IntegrationTypes.award) {
      integration = new IntegrationAwardModel();
    } else {
      integration = new IntegrationModel();
    }

    integration.fill(json);
    return integration;

  }

  static generateModels(jsonList: any[]): IntegrationModel[] {
    const list = [];
    for (const json of jsonList) {
      list.push(this.generateModel(json));
    }
    return list;
  }

  /**
   * Fills the fields of this integration from json. Subclasses extend it with their own fields.
   */
  protected fill(json: any) {

    this._id = json._id;
    this.name = json.name;
    this.integrationType = json.integrationType;
    this.visualType = json.visualType || 'qrcode';
    this.contentType = json.contentType;
    this.country = json.country;
    this.region = json.region;
    this.minimumGameVersionNumber = json.minimumGameVersionNumber;
    this.rewardGrantType = json.rewardGrantType || RewardGrantTypes.grantAll;
    this.numUsesPerStudent = json.numUsesPerStudent || 1;

    if (json.redeemEndAt) {
      this.redeemEndAt = new Date(json.redeemEndAt);
    }

    if (json.rewards && json.rewards.length > 0) {
      this.rewards = RewardModel.generateModels(json.rewards);
    }

    if (json.createdAt) {
      this.createdAt = new Date(json.createdAt);
    }

    if (json.updatedAt) {
      this.updatedAt = new Date(json.updatedAt);
    }

  }

  hasRedeemDeadlinePassed(): boolean {
    return this.redeemEndAt !== null && this.redeemEndAt.getTime() < Date.now();
  }

}

/**
 * A standalone award, not tied to a competition or a goal.
 */
export class IntegrationAwardModel extends IntegrationModel {
  public awardType: string = CompetitionAwardTypes.award;
  public numQuestions = 10;
  public obstacleCourseLevel = ObstacleCourseLevels.none;

  static generateModel(json: any): IntegrationAwardModel {
    const integration = new IntegrationAwardModel();
    integration.fill(json);
    return integration;
  }

  protected fill(json: any) {
    super.fill(json);
    this.awardType = json.awardType || CompetitionAwardTypes.award;
    this.numQuestions = json.numQuestions || 10;
    this.obstacleCourseLevel = json.obstacleCourseLevel || ObstacleCourseLevels.none;
  }

  isObstacleCourse(): boolean {
    return this.awardType === CompetitionAwardTypes.obstacleCourse;
  }
}

/**
 * An award that can be linked to a competition and one of its goals.
 */
export class IntegrationCompetitionAwardModel extends IntegrationAwardModel {
  public competitionId: string;
  public competitionGoalId: string;

  static generateModel(json: any): IntegrationCompetitionAwardModel {
    const integration = new IntegrationCompetitionAwardModel();
    integration.fill(json);
    return integration;
  }

  protected fill(json: any) {
    super.fill(json);
    this.competitionId = json.competitionId;
    this.competitionGoalId = json.competitionGoalId;
  }
}

export class IntegrationFilter {
  skip = 0;
  limit = 20;
  name: string = undefined;
  integrationType: string = undefined;
  competitionId: string = undefined;
  competitionGoalId: string = undefined;
}

export class IntegrationCompetitionAwardHelper {

  static isObstacleCourse(integration: IntegrationModel): boolean {
    const award = integration as IntegrationAwardModel;
    return award.awardType === CompetitionAwardTypes.obstacleCourse;
  }

}

